<template>
    <div class="ls-service-details">
        <div>
            <LsServiceStatusIndicator
                v-if="showStatusIndicator"
                :service="service"
                :disable-tooltip="disableTooltip"
            />
            <span
                :class="{
                    'ls-service-details--clickable': hasClickListener,
                    'ls-service-details--contractor-details': showContractorDetails,
                }"
                class="ls-service-details--service-name"
                test-id="service_name"
                @click="handleClick"
                >{{ serviceName }}</span
            >
            <LsServiceTypePill :service="service" />
        </div>
        <span
            v-if="service.schedule_at"
            :class="
                showContractorDetails
                    ? 'ls-service-details--contractor-details'
                    : 'ls-service-details--date'
            "
            >{{ service.schedule_at | date('monthDateYear') }}</span
        >
        <span
            v-else
            :class="
                showContractorDetails
                    ? 'ls-service-details--contractor-details'
                    : 'ls-service-details--date'
            "
            >{{ dateRange }} ({{ service.cycle | frequency }})</span
        >
        <div
            v-if="showContractorDetails && contractorName"
            class="ls-service-details--contractor-details"
        >
            {{ contractorName }}
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types';
import { lsScheduleHelpers, lsFilters } from 'ls-vue-common';
import _ from 'lodash';
import LsServiceStatusIndicator from '../LsServiceStatusIndicator/LsServiceStatusIndicator.vue';
import LsServiceTypePill from '../LsServiceTypePill/LsServiceTypePill.vue';
import { lsHelpers } from '@/helpers';

export default {
    name: 'LsServiceDetails',
    components: { LsServiceStatusIndicator, LsServiceTypePill },
    props: {
        service: VueTypes.shape({
            __typename: VueTypes.oneOf(['Schedule', 'Recommendation', 'RequiredService'])
                .isRequired,
            id: VueTypes.string.isRequired,
            cycle: VueTypes.integer,
            contractor: VueTypes.oneOfType([
                VueTypes.shape({
                    id: VueTypes.string.isRequired,
                    name: VueTypes.string.isRequired,
                    user: VueTypes.shape({
                        first_name: VueTypes.string.isRequired,
                        last_name: VueTypes.string.isRequired,
                    }).loose,
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
            // schedules
            next_customer_starts_at: VueTypes.string,
            next_customer_ends_at: VueTypes.string,
            // recommendations / requiredServices
            schedule_at: VueTypes.string,
        }).isRequired.loose,
        disableTooltip: VueTypes.bool.def(false),
        showStatusIndicator: VueTypes.bool.def(true),
        showContractorDetails: VueTypes.bool.def(false),
    },
    computed: {
        hasClickListener() {
            return this.$listeners && this.$listeners.click;
        },
        serviceName() {
            return lsScheduleHelpers.getScheduleServiceName({
                schedule: this.service,
                nameOnly: true,
            });
        },
        dateRange() {
            const { next_customer_starts_at, next_customer_ends_at } = this.service;
            return lsHelpers.dateRange({
                start: next_customer_starts_at,
                end: next_customer_ends_at,
            });
        },
        contractorName() {
            if (_.isNil(this.service.contractor)) {
                return null;
            }

            const { user, name } = this.service.contractor;
            return `${name} - ${lsFilters.fullName({ user })}`;
        },
    },
    methods: {
        handleClick() {
            const { id, __typename } = this.service;
            this.$emit('click', { id, __typename });
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-service-details {
    color: $secondary-dark;
    div {
        display: flex;
        align-items: center;
        height: 20px;
        padding-bottom: 2px;
        span {
            margin: 0 4px;
            font-weight: $medium;
        }
    }
}

.ls-service-details--service-name {
    overflow: hidden;
    text-overflow: ellipsis;
}

.ls-service-details--date {
    font-size: 1.2rem;
    padding-left: 24px;
}

.ls-service-details--contractor-details {
    align-items: normal !important;
    font-size: $size-5;
    margin: 2px 4px;
    color: $secondary;
}

.ls-service-details--clickable {
    color: $purple;
    font-weight: $regular !important;
    cursor: pointer;
    &:hover {
        opacity: $hover-opacity;
        user-select: none;
    }
}
</style>
